/**
 * A conversa com o Kovu.
 *
 * A conversa mora em `ai_conversations/assistant`, o mesmo documento que o
 * Flutter grava (`AIConversation.toJson`): quem começa a falar no celular
 * continua aqui, e é por isso que o formato é copiado campo a campo em vez de
 * inventado.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  COLECOES,
  gravarData,
  hora,
  lerData,
  novoId,
  traduzir,
  usarColecao,
} from '@nosso-sempre/shared';
import { api, armazem } from '../nucleo/api';
import { Aviso, Titulo, Vazio } from '../componentes/Pecas';
import { Icone } from '../componentes/Icone';
import cara from '../imagens/kovu.webp';
import './kovu.css';

const RECURSO = 'assistant';

interface Fala {
  role: string;
  content: string;
  timestamp?: string;
}

interface DocConversa {
  id?: string;
  feature?: string;
  messages?: Fala[];
  createdAt?: string;
  updatedAt?: string;
}

export function Kovu() {
  const conversas = usarColecao<DocConversa>(COLECOES.conversas);
  const guardada = conversas.find((c) => c.id === RECURSO);

  const [falas, setFalas] = useState<Fala[]>([]);
  const [rascunho, setRascunho] = useState('');
  const [vindo, setVindo] = useState<string | null>(null);
  const [recado, setRecado] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  // A conversa guardada entra uma vez. Depois disso o que está na tela manda:
  // o armazém devolve o eco da nossa própria gravação, e reagir a ele apagaria
  // a resposta que ainda está chegando.
  const jaVeio = useRef(false);
  useEffect(() => {
    if (jaVeio.current || !guardada) return;
    jaVeio.current = true;
    setFalas(guardada.dados.messages ?? []);
  }, [guardada]);

  const fim = useRef<HTMLDivElement>(null);
  useEffect(() => {
    fim.current?.scrollIntoView({ block: 'end' });
  }, [falas, vindo]);

  const cancelar = useRef<(() => void) | null>(null);
  useEffect(() => () => cancelar.current?.(), []);

  const ocupado = vindo !== null;

  const criadaEm = useMemo(() => guardada?.dados.createdAt ?? gravarData(), [guardada]);

  async function guardar(todas: Fala[]) {
    await armazem.gravar(COLECOES.conversas, RECURSO, {
      id: RECURSO,
      feature: RECURSO,
      messages: todas,
      createdAt: criadaEm,
      updatedAt: gravarData(),
    });
  }

  function enviar(e: React.FormEvent) {
    e.preventDefault();
    const texto = rascunho.trim();
    if (!texto || ocupado) return;

    const minha: Fala = { role: 'user', content: texto, timestamp: gravarData() };
    const comMinha = [...falas, minha];
    setFalas(comMinha);
    setRascunho('');
    setErro(null);
    setRecado(null);
    setVindo('');

    let juntando = '';
    cancelar.current = api.kovuEmPedacos(
      comMinha.map((f) => ({ role: f.role, content: f.content })),
      (pedaco) => {
        juntando += pedaco;
        setVindo(juntando);
      },
      () => {
        cancelar.current = null;
        setVindo(null);
        setRecado(null);
        if (!juntando.trim()) {
          setErro(traduzir('O Kovu não disse nada. Tente de novo.'));
          return;
        }
        const dele: Fala = {
          role: 'assistant',
          content: juntando,
          timestamp: gravarData(),
        };
        const todas = [...comMinha, dele];
        setFalas(todas);
        void guardar(todas);
      },
      (falha) => {
        cancelar.current = null;
        setVindo(null);
        setRecado(null);
        setErro(falha.message);
      },
      {},
      (status) => setRecado(status),
    );
  }

  /**
   * Recomeça a conversa. Guarda o que passou em `ai_sessions` antes de apagar,
   * como o Flutter faz: sem isso, "nova conversa" viraria "conversa perdida".
   */
  async function recomecar() {
    cancelar.current?.();
    cancelar.current = null;
    setVindo(null);
    if (falas.length >= 2) {
      await armazem.gravar(COLECOES.sessoes, novoId(), {
        feature: RECURSO,
        messages: falas,
        messageCount: falas.length,
        hasSummary: false,
        createdAt: gravarData(),
      });
    }
    setFalas([]);
    setErro(null);
    await armazem.apagar(COLECOES.conversas, RECURSO).catch(() => {
      // Apagar o que já não existe não é problema de ninguém.
    });
  }

  return (
    <div className="conversa">
      <Titulo
        icone="kovu"
        cor="var(--lavanda-viva)"
        acao={
          /* Os três atalhos do `kovu_widgets.dart`, na mesma ordem, com o
             risquinho de 1 por 20 separando-os da nova conversa. */
          <span className="kovu-atalhos">
            <Link className="kovu-atalho" to="/kovu-memory" title={traduzir('Livro de Memórias')}>
              <Icone nome="livro-aberto" tamanho={20} rotulo={traduzir('Livro de Memórias')} />
            </Link>
            <Link className="kovu-atalho" to="/kovu-trivia" title={traduzir('Quiz de Memórias')}>
              <Icone nome="quiz" tamanho={20} rotulo={traduzir('Quiz de Memórias')} />
            </Link>
            <Link className="kovu-atalho" to="/kovu-today" title={traduzir('Kovu Hoje')}>
              <Icone nome="amanhecer" tamanho={20} rotulo={traduzir('Kovu Hoje')} />
            </Link>
            {falas.length > 0 ? (
              <>
                <span className="kovu-risco" aria-hidden="true" />
                <button
                  className="capa-acao"
                  type="button"
                  onClick={() => void recomecar()}
                  title={traduzir('Nova conversa')}
                >
                  <Icone nome="mais" tamanho={20} rotulo={traduzir('Nova conversa')} />
                </button>
              </>
            ) : null}
          </span>
        }
      >
        {traduzir('Kovu')}
      </Titulo>

      <div className="falas" role="log" aria-live="polite" aria-label={traduzir('Conversa')}>
        {falas.length === 0 && !ocupado ? (
          <Vazio
            icone="kovu"
            titulo={traduzir('O Kovu está aqui')}
            recado={traduzir('Pergunte qualquer coisa sobre a casa, os planos ou o dia.')}
          />
        ) : null}

        {falas.map((f, i) => (
          <Balao key={`${f.timestamp ?? ''}-${i}`} fala={f} />
        ))}

        {vindo !== null ? (
          <div className="fala dele">
            <img className="kovu-cara" src={cara} alt="" />
            <div className="balao">
              <p>{vindo || <span className="discreto">…</span>}</p>
              {recado ? <span className="discreto">{recado}</span> : null}
            </div>
          </div>
        ) : null}

        <div ref={fim} />
      </div>

      {erro ? <Aviso>{erro}</Aviso> : null}

      <form className="dizer" onSubmit={enviar}>
        <label className="so-leitor" htmlFor="dizer">
          {traduzir('Escreva para o Kovu')}
        </label>
        <textarea
          id="dizer"
          rows={1}
          placeholder={traduzir('Escreva aqui')}
          value={rascunho}
          onChange={(e) => setRascunho(e.target.value)}
          onKeyDown={(e) => {
            // Enter manda, Shift+Enter pula linha: é o que a mão já espera.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              e.currentTarget.form?.requestSubmit();
            }
          }}
        />
        {ocupado ? (
          <button
            className="botao calmo"
            type="button"
            onClick={() => {
              cancelar.current?.();
              cancelar.current = null;
              setVindo(null);
              setRecado(null);
            }}
          >
            <Icone nome="fechar" rotulo={traduzir('Parar')} />
          </button>
        ) : (
          <button className="botao" type="submit" disabled={!rascunho.trim()}>
            <Icone nome="enviar" rotulo={traduzir('Enviar')} />
          </button>
        )}
      </form>
    </div>
  );
}

/**
 * Uma fala. Quando é do Kovu, a cara dele vem antes do balão — é assim que o
 * `_MessageBubble` do Flutter distingue quem fala, junto com o canto reto que
 * aponta para o lado de quem disse.
 */
function Balao({ fala }: { fala: Fala }) {
  const quando = lerData(fala.timestamp);
  const minha = fala.role === 'user';
  return (
    <div className={`fala ${minha ? 'minha' : 'dele'}`}>
      {minha ? null : <img className="kovu-cara" src={cara} alt="" />}
      <div className="balao">
        <p>{fala.content}</p>
        {quando ? <time className="discreto">{hora(quando)}</time> : null}
      </div>
    </div>
  );
}
